/**
 * TanStack Query Configuration
 * Sets up the QueryClient with dynamic API configuration and error handling
 */

import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * Global error handler for TanStack Query
 */
const queryErrorHandler = (error) => {
  // Error instances may carry request/response data and private stack text.
  // Keep diagnostic categories and numeric API status only.
  if (error instanceof ApiError && Number.isInteger(error.status)) {
    console.error("Query Error:", error.status);
  } else {
    console.error("Query Error:");
  }

  // Handle API errors specifically
  if (error instanceof ApiError) {
    // Handle authentication errors
    if (error.status === 401) {
      // Clear user session and redirect to login
      if (typeof window !== "undefined") {
        localStorage.removeItem("auth_token");
        window.location.href = "/login";
      }
    }

    // Handle other API errors
    switch (error.status) {
      case 403:
        console.error("Access forbidden:");
        break;
      case 404:
        console.error("Resource not found:");
        break;
      case 500:
        console.error("Server error:");
        break;
      default:
        console.error("API Error:");
    }
  }
};

/**
 * Create and configure QueryClient
 */
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: How long data is considered fresh (5 minutes)
        staleTime: 5 * 60 * 1000,

        // Cache time: How long data stays in cache (10 minutes)
        gcTime: 10 * 60 * 1000,

        // Retry configuration
        retry: false,
        // retry: (failureCount, error) => {
        //   // Don't retry on 4xx errors (client errors)
        //   if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        //     return false;
        //   }
        //   // Retry up to 3 times for other errors
        //   return failureCount < 3;
        // },

        // Retry delay with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Refetch on window focus - DISABLED to prevent multiple fetches
        refetchOnWindowFocus: false,

        // Refetch on reconnect - DISABLED to prevent multiple fetches
        refetchOnReconnect: false,

        // Refetch on mount - only when data is stale
        refetchOnMount: "always",

        // Network mode - only online
        networkMode: "online",

        // Error handling
        onError: queryErrorHandler,
      },
      mutations: {
        // Error handling for mutations
        onError: queryErrorHandler,

        // Retry once only a failed GET. Any other method may have taken
        // effect before failing, and sending it again can repeat it or spend
        // a single-use value such as an OAuth code.
        retry: (failureCount, error) =>
          failureCount < 1 &&
          error instanceof ApiError &&
          error.method === "GET",
      },
    },
  });
};

/**
 * Query Keys Factory
 * Centralized query key management for better cache invalidation
 */
export const queryKeys = {
  // User related queries
  user: {
    all: ["user"],
    profile: () => [...queryKeys.user.all, "profile"],
    permissions: () => [...queryKeys.user.all, "permissions"],
    sessions: (userId) => [...queryKeys.user.all, "sessions", userId],
  },

  // Future query keys will be added here as needed
  // Example structure for future features:
  // products: {
  //   all: ['products'],
  //   lists: () => [...queryKeys.products.all, 'list'],
  //   list: (filters) => [...queryKeys.products.lists(), { filters }],
  //   details: () => [...queryKeys.products.all, 'detail'],
  //   detail: (id) => [...queryKeys.products.details(), id],
  // },
};

/**
 * Default QueryClient instance
 */
export const queryClient = createQueryClient();
